import { db } from '../firebase/config';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';

// Permission levels
export type UserRole = 'owner' | 'manager' | 'staff';
export type Permission = 'business-settings' | 'credit-percentages' | 'add-staff' | 'edit-staff' | 'delete-staff' | 'view-business';

// Get user's role in a specific business
export const getUserRole = async (userId: string, businessId: string): Promise<UserRole | null> => {
  try {
    // First check if user is explicitly listed as staff
    const staffQuery = query(
      collection(db, 'staff'),
      where('userId', '==', userId),
      where('businessId', '==', businessId)
    );
    
    const staffSnapshot = await getDocs(staffQuery);
    
    if (!staffSnapshot.empty) {
      const staffData = staffSnapshot.docs[0].data();
      return staffData.role as UserRole;
    }
    
    // Check if user is the business creator/manager (owner privileges)
    const businessDoc = await getDoc(doc(db, 'businesses', businessId));
    if (businessDoc.exists()) {
      const businessData = businessDoc.data();
      if (businessData.managerId === userId) {
        return 'owner'; // Business creator gets owner privileges
      }
    }
    
    return null; // User has no role in this business
  } catch (error) {
    console.error('Error getting user role:', error);
    return null;
  }
};

// Check if user has specific permission for a business
export const hasPermission = async (userId: string, businessId: string, permission: Permission): Promise<boolean> => {
  try {
    const userRole = await getUserRole(userId, businessId);
    
    if (!userRole) {
      return false; // No role = no permissions
    }
    
    // Permission matrix
    const permissions: Record<UserRole, Permission[]> = {
      'owner': ['business-settings', 'credit-percentages', 'add-staff', 'edit-staff', 'delete-staff', 'view-business'],
      'manager': ['business-settings', 'credit-percentages', 'add-staff', 'edit-staff', 'delete-staff', 'view-business'],
      'staff': ['view-business'] // Staff can only view business listings
    };
    
    return permissions[userRole].includes(permission);
  } catch (error) {
    console.error('Error checking permission:', error);
    return false;
  }
};

// Check if user can manage staff (for team management pages)
export const canManageStaff = async (userId: string, businessId: string): Promise<boolean> => {
  return hasPermission(userId, businessId, 'add-staff');
};

// Check if user can access business settings
export const canAccessBusinessSettings = async (userId: string, businessId: string): Promise<boolean> => {
  return hasPermission(userId, businessId, 'business-settings');
};

// Check if user can manage credit percentages
export const canManageCreditPercentages = async (userId: string, businessId: string): Promise<boolean> => {
  return hasPermission(userId, businessId, 'credit-percentages');
};

// Get permission error message for display
export const getPermissionErrorMessage = (permission: Permission): string => {
  const messages: Record<Permission, string> = {
    'business-settings': 'You do not have permission to access business settings. Contact your business owner or manager.',
    'credit-percentages': 'You do not have permission to manage credit percentages. Contact your business owner or manager.',
    'add-staff': 'You do not have permission to add team members. Contact your business owner or manager.',
    'edit-staff': 'You do not have permission to edit team members. Contact your business owner or manager.',
    'delete-staff': 'You do not have permission to remove team members. Contact your business owner or manager.',
    'view-business': 'You do not have permission to view this business.'
  };
  
  return messages[permission] || 'You do not have permission to perform this action.';
};

// Role hierarchy (higher number = higher authority)
const roleHierarchy: Record<UserRole, number> = {
  'staff': 1,
  'manager': 2,
  'owner': 3
};

// Check if user can assign a specific role (can't assign roles higher than their own)
export const canAssignRole = async (userId: string, businessId: string, targetRole: UserRole): Promise<boolean> => {
  try {
    const userRole = await getUserRole(userId, businessId);
    
    if (!userRole) {
      return false; // No role = no permissions
    }
    
    // Users can only assign roles that are equal to or lower than their own
    return roleHierarchy[userRole] >= roleHierarchy[targetRole];
  } catch (error) {
    console.error('Error checking role assignment permission:', error);
    return false;
  }
};

// Get available roles that a user can assign
export const getAvailableRoles = async (userId: string, businessId: string): Promise<UserRole[]> => {
  try {
    const userRole = await getUserRole(userId, businessId);
    
    if (!userRole) {
      return []; // No role = no permissions
    }
    
    const userHierarchy = roleHierarchy[userRole];
    const availableRoles: UserRole[] = [];
    
    // Add all roles that are equal to or lower than user's role
    for (const [role, hierarchy] of Object.entries(roleHierarchy)) {
      if (hierarchy <= userHierarchy) {
        availableRoles.push(role as UserRole);
      }
    }
    
    return availableRoles.sort((a, b) => roleHierarchy[b] - roleHierarchy[a]); // Sort by hierarchy (highest first)
  } catch (error) {
    console.error('Error getting available roles:', error);
    return [];
  }
};

// Get role assignment error message
export const getRoleAssignmentErrorMessage = (userRole: UserRole | null, targetRole: UserRole): string => {
  if (!userRole) {
    return 'You do not have permission to assign roles.';
  }
  
  const roleNames: Record<UserRole, string> = {
    'owner': 'Owner',
    'manager': 'Manager',
    'staff': 'Staff'
  };
  
  return `You cannot assign the ${roleNames[targetRole]} role. As a ${roleNames[userRole]}, you can only assign roles equal to or lower than your own.`;
};
